import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getLocations } from '../../services/locationService';
import { getShifts } from '../../services/shiftService';
import {
  getHolidayCalendar,
  getHolidayCalendars,
} from '../../services/holidayCalendarService';
import { getLateArrivalConfiguration } from '../../services/lateArrivalConfigurationService';
import {
  createWorkPolicy,
  getWorkPolicyDetails,
  updateWorkPolicy,
} from '../../services/workPolicyService';
import type { WorkPolicyDetails } from '../../types/workPolicy';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import {
  isConcurrencyConflict,
  shiftWorkPolicyErrorMessage,
} from '../../utils/shiftWorkPolicyErrors';
import {
  matchingSpreadMinutes,
  minutesFromFormValue,
  parseNonNegativeDuration,
  parsePositiveDuration,
} from '../../utils/workDuration';
import { DURATION_INPUT_HINT, formatMinutes } from '../../utils/dateTime';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { WorkDurationFields } from '../../components/WorkDurationFields';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<
  RootStackParamList,
  'WorkPolicyCreate'
>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'WorkPolicyEdit'>;
type Props = CreateProps | EditProps;

const MISSING_CHECKOUT_POLICIES = [
  'MARK_MISSING_CHECKOUT',
  'AUTO_CHECKOUT_AT_SHIFT_END',
  'AUTO_CHECKOUT_AFTER_LIMIT',
  'REQUIRE_CORRECTION',
] as const;

type FormState = {
  policyCode: string;
  policyName: string;
  description: string;
  locationId: number | null;
  locationLabel: string | null;
  defaultShiftId: number | null;
  defaultShiftLabel: string | null;
  holidayCalendarId: number | null;
  holidayCalendarLabel: string | null;
  expectedWorkMinutes: string;
  lunchBreakMinutes: string;
  spreadMinutes: string;
  minimumHalfDayMinutes: string;
  lateGraceMinutes: string;
  earlyExitGraceMinutes: string;
  shortHoursToleranceMinutes: string;
  maximumLateMinutes: string;
  maximumEarlyExitMinutes: string;
  workingDaysPerMonth: string;
  allowMultiplePunches: boolean;
  allowManualAttendance: boolean;
  allowAttendanceCorrection: boolean;
  wfhEligible: boolean;
  permissionEnabled: boolean;
  overtimeEnabled: boolean;
  overtimeAfterMinutes: string;
  maximumOvertimeMinutes: string;
  missingCheckoutPolicy: string;
  isDefault: boolean;
  isActive: boolean;
};

const EMPTY: FormState = {
  policyCode: '',
  policyName: '',
  description: '',
  locationId: null,
  locationLabel: null,
  defaultShiftId: null,
  defaultShiftLabel: null,
  holidayCalendarId: null,
  holidayCalendarLabel: null,
  expectedWorkMinutes: '',
  lunchBreakMinutes: '',
  spreadMinutes: '',
  minimumHalfDayMinutes: '',
  lateGraceMinutes: formatMinutes(10),
  earlyExitGraceMinutes: formatMinutes(10),
  shortHoursToleranceMinutes: formatMinutes(15),
  maximumLateMinutes: formatMinutes(120),
  maximumEarlyExitMinutes: formatMinutes(120),
  workingDaysPerMonth: '22',
  allowMultiplePunches: true,
  allowManualAttendance: false,
  allowAttendanceCorrection: true,
  wfhEligible: false,
  permissionEnabled: false,
  overtimeEnabled: false,
  overtimeAfterMinutes: '',
  maximumOvertimeMinutes: '',
  missingCheckoutPolicy: 'MARK_MISSING_CHECKOUT',
  isDefault: false,
  isActive: true,
};

function durationOrEmpty(value: number | null | undefined): string {
  if (value == null) return '';
  return formatMinutes(value);
}

function fromDetails(item: WorkPolicyDetails): FormState {
  return {
    policyCode: item.policyCode,
    policyName: item.policyName,
    description: item.description ?? '',
    locationId: item.locationId,
    locationLabel: item.locationName,
    defaultShiftId: item.defaultShiftId,
    defaultShiftLabel:
      item.defaultShiftName ||
      (item.defaultShiftId ? `Shift #${item.defaultShiftId}` : null),
    holidayCalendarId: item.holidayCalendarId,
    holidayCalendarLabel:
      item.holidayCalendarId != null
        ? `Calendar #${item.holidayCalendarId}`
        : null,
    expectedWorkMinutes: durationOrEmpty(item.expectedWorkMinutes),
    lunchBreakMinutes: formatMinutes(item.lunchBreakMinutes),
    spreadMinutes: durationOrEmpty(item.spreadMinutes),
    minimumHalfDayMinutes: durationOrEmpty(item.minimumHalfDayMinutes),
    lateGraceMinutes: formatMinutes(item.lateGraceMinutes),
    earlyExitGraceMinutes: formatMinutes(item.earlyExitGraceMinutes),
    shortHoursToleranceMinutes: formatMinutes(item.shortHoursToleranceMinutes),
    maximumLateMinutes: formatMinutes(item.maximumLateMinutes),
    maximumEarlyExitMinutes: formatMinutes(item.maximumEarlyExitMinutes),
    workingDaysPerMonth:
      item.workingDaysPerMonth != null ? String(item.workingDaysPerMonth) : '',
    allowMultiplePunches: item.allowMultiplePunches,
    allowManualAttendance: item.allowManualAttendance,
    allowAttendanceCorrection: item.allowAttendanceCorrection,
    wfhEligible: item.wfhEligible,
    permissionEnabled: item.permissionEnabled,
    overtimeEnabled: item.overtimeEnabled,
    overtimeAfterMinutes: durationOrEmpty(item.overtimeAfterMinutes),
    maximumOvertimeMinutes: durationOrEmpty(item.maximumOvertimeMinutes),
    missingCheckoutPolicy: item.missingCheckoutPolicy ?? '',
    isDefault: item.isDefault,
    isActive: item.isActive !== false,
  };
}

function parsePositiveInt(value: string, label: string): number | string {
  const number = Number(value);
  if (!Number.isInteger(number) || number <= 0) {
    return `${label} must be a positive integer.`;
  }
  return number;
}

export function validateWorkPolicyForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.policyCode.trim()) return 'Policy code is required.';
  if (form.policyCode.trim().length > 50) return 'Policy code is too long.';
  if (!form.policyName.trim()) return 'Policy name is required.';
  if (form.policyName.trim().length > 150) return 'Policy name is too long.';
  if (form.description.trim().length > 500) {
    return 'Description is too long.';
  }
  if (form.defaultShiftId == null || form.defaultShiftId <= 0) {
    return 'Default shift is required.';
  }
  if (!form.missingCheckoutPolicy.trim()) {
    return 'Missing checkout policy is required.';
  }
  if (form.missingCheckoutPolicy.trim().length > 30) {
    return 'Missing checkout policy is too long.';
  }

  const expected = parsePositiveDuration(
    form.expectedWorkMinutes,
    'Expected work duration',
  );
  if (typeof expected === 'string') return expected;
  if (form.lunchBreakMinutes.trim() === '') {
    return 'Lunch break duration is required.';
  }
  const lunch = parseNonNegativeDuration(
    form.lunchBreakMinutes,
    'Lunch break duration',
  );
  if (typeof lunch === 'string') return lunch;
  if (form.spreadMinutes.trim() === '') {
    return 'Spread duration is required.';
  }
  const spread = parsePositiveDuration(form.spreadMinutes, 'Spread duration');
  if (typeof spread === 'string') return spread;
  if (!matchingSpreadMinutes(expected, lunch, spread)) {
    return 'Spread duration must equal expected work duration plus lunch break duration.';
  }
  if (spread > 24 * 60) {
    return 'Spread duration cannot exceed 24 hours.';
  }
  const halfDay = parsePositiveDuration(
    form.minimumHalfDayMinutes,
    'Minimum half day',
  );
  if (typeof halfDay === 'string') return halfDay;
  if (halfDay >= expected) {
    return 'Minimum half day must be less than expected work duration.';
  }

  const workingDays = parsePositiveInt(
    form.workingDaysPerMonth,
    'Working days per month',
  );
  if (typeof workingDays === 'string') return workingDays;
  if (workingDays > 31) {
    return 'Working days per month cannot exceed 31.';
  }

  const missingCheckoutPolicy = form.missingCheckoutPolicy
    .trim()
    .toUpperCase();
  if (
    !MISSING_CHECKOUT_POLICIES.includes(
      missingCheckoutPolicy as (typeof MISSING_CHECKOUT_POLICIES)[number],
    )
  ) {
    return 'Select a valid missing checkout policy.';
  }

  for (const [value, label] of [
    [form.lateGraceMinutes, 'Late grace'],
    [form.earlyExitGraceMinutes, 'Early exit grace'],
    [form.shortHoursToleranceMinutes, 'Short hours tolerance'],
    [form.maximumLateMinutes, 'Maximum late'],
    [form.maximumEarlyExitMinutes, 'Maximum early exit'],
  ] as const) {
    const parsed = parseNonNegativeDuration(value, label);
    if (typeof parsed === 'string') return parsed;
  }

  if (
    form.holidayCalendarId != null &&
    (!Number.isInteger(form.holidayCalendarId) || form.holidayCalendarId <= 0)
  ) {
    return 'Select a valid holiday calendar.';
  }

  if (form.overtimeEnabled) {
    const after = parsePositiveDuration(
      form.overtimeAfterMinutes,
      'Overtime after',
    );
    if (typeof after === 'string') return after;
    const max = parseNonNegativeDuration(
      form.maximumOvertimeMinutes,
      'Maximum overtime',
    );
    if (typeof max === 'string') return max;
    if (form.maximumOvertimeMinutes.trim() && max < after) {
      return 'Maximum overtime cannot be less than overtime after.';
    }
  }

  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'WorkPolicyEdit';
}

export function WorkPolicyFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      switchLabel: {
        ...typography.body,
        color: palette.textPrimary,
        flex: 1,
        paddingRight: spacing.md,
      },
      error: { ...typography.caption, color: palette.error },
      hint: { ...typography.caption, color: palette.textSecondary },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const workPolicyId = isEdit ? props.route.params.workPolicyId : null;
  const navigation = props.navigation;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<WorkPolicyDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!workPolicyId) return;
    setLoading(true);
    try {
      const data = await getWorkPolicyDetails(workPolicyId);
      setDetails(data);
      const nextForm = fromDetails(data);
      if (data.holidayCalendarId != null) {
        try {
          const calendar = await getHolidayCalendar(data.holidayCalendarId);
          nextForm.holidayCalendarLabel = calendar.calendarName;
        } catch {
          // Keep the fallback label from fromDetails when lookup fails.
        }
      }
      setForm(nextForm);
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        shiftWorkPolicyErrorMessage(
          caught,
          'The requested work policy could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [workPolicyId]);

  const loadCreateDefaults = useCallback(async () => {
    if (!isTimePermissionEnabled()) return;
    try {
      const lateArrival = await getLateArrivalConfiguration();
      setForm(current => ({
        ...current,
        permissionEnabled: lateArrival.isEnabled,
      }));
    } catch {
      setForm(current => ({ ...current, permissionEnabled: false }));
    }
  }, []);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
    else loadCreateDefaults().catch(() => undefined);
  }, [isEdit, load, loadCreateDefaults]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  const loadLocationOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getLocations({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.locationId,
        label: item.locationName,
        subtitle: item.locationCode,
      }));
    },
    [],
  );

  const loadShiftOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getShifts({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.shiftId,
        label: item.shiftName,
        subtitle: item.shiftCode,
      }));
    },
    [],
  );

  const loadHolidayCalendarOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getHolidayCalendars({
        search: search || undefined,
        isActive: true,
        page: 1,
        pageSize: 20,
      });
      return result.items.map(item => ({
        id: item.holidayCalendarId,
        label: item.calendarName,
        subtitle: item.calendarCode,
      }));
    },
    [],
  );

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  async function submit() {
    if (savingLock.current) return;
    const validation = validateWorkPolicyForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    const payload = {
      locationId: form.locationId,
      defaultShiftId: form.defaultShiftId as number,
      holidayCalendarId: form.holidayCalendarId,
      policyName: form.policyName,
      description: form.description,
      expectedWorkMinutes: minutesFromFormValue(form.expectedWorkMinutes) ?? 0,
      lunchBreakMinutes: minutesFromFormValue(form.lunchBreakMinutes) ?? 0,
      minimumHalfDayMinutes:
        minutesFromFormValue(form.minimumHalfDayMinutes) ?? 0,
      lateGraceMinutes: minutesFromFormValue(form.lateGraceMinutes) ?? 0,
      earlyExitGraceMinutes:
        minutesFromFormValue(form.earlyExitGraceMinutes) ?? 0,
      shortHoursToleranceMinutes:
        minutesFromFormValue(form.shortHoursToleranceMinutes) ?? 0,
      maximumLateMinutes: minutesFromFormValue(form.maximumLateMinutes) ?? 0,
      maximumEarlyExitMinutes:
        minutesFromFormValue(form.maximumEarlyExitMinutes) ?? 0,
      workingDaysPerMonth: Number(form.workingDaysPerMonth),
      allowMultiplePunches: form.allowMultiplePunches,
      allowManualAttendance: form.allowManualAttendance,
      allowAttendanceCorrection: form.allowAttendanceCorrection,
      wfhEligible: form.wfhEligible,
      permissionEnabled: form.permissionEnabled,
      overtimeEnabled: form.overtimeEnabled,
      overtimeAfterMinutes: form.overtimeEnabled
        ? minutesFromFormValue(form.overtimeAfterMinutes)
        : null,
      maximumOvertimeMinutes: form.overtimeEnabled
        ? form.maximumOvertimeMinutes.trim()
          ? minutesFromFormValue(form.maximumOvertimeMinutes)
          : null
        : null,
      missingCheckoutPolicy: form.missingCheckoutPolicy.trim().toUpperCase(),
      isDefault: form.isDefault,
    };

    try {
      if (isEdit && workPolicyId && details) {
        await runOperation('update', () => updateWorkPolicy(workPolicyId, {
          ...payload,
          displayOrder: details.displayOrder,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createWorkPolicy({
          ...payload,
          policyCode: form.policyCode,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = shiftWorkPolicyErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          shiftWorkPolicyErrorMessage(
            caught,
            isEdit
              ? 'Unable to update work policy.'
              : 'Unable to create work policy.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.WORK_POLICY_UPDATE
    : PERMISSIONS.WORK_POLICY_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit work policy' : 'Create work policy'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading work policy" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit work policy"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit work policy' : 'Create work policy'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Policy code</Text>
                <Text style={themed.readOnlyValue}>{form.policyCode}</Text>
              </>
            ) : (
              <AppInput
                label="Policy code *"
                value={form.policyCode}
                onChangeText={value => setField('policyCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Policy name *"
              value={form.policyName}
              onChangeText={value => setField('policyName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => setField('description', value)}
            />
            <SearchableEntitySelector
              label="Default shift *"
              selectedValue={form.defaultShiftId}
              selectedLabel={form.defaultShiftLabel}
              loadOptions={loadShiftOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  defaultShiftId: option.id,
                  defaultShiftLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  defaultShiftId: null,
                  defaultShiftLabel: null,
                }));
              }}
              placeholder="Search shifts"
            />
            <SearchableEntitySelector
              label="Location"
              selectedValue={form.locationId}
              selectedLabel={form.locationLabel}
              loadOptions={loadLocationOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: option.id,
                  locationLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: null,
                  locationLabel: null,
                }));
              }}
              placeholder="Optional location"
            />
            <SearchableEntitySelector
              label="Holiday calendar"
              selectedValue={form.holidayCalendarId}
              selectedLabel={form.holidayCalendarLabel}
              loadOptions={loadHolidayCalendarOptions}
              emptyMessage="No data available"
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  holidayCalendarId: option.id,
                  holidayCalendarLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  holidayCalendarId: null,
                  holidayCalendarLabel: null,
                }));
              }}
              placeholder="Search holiday calendars"
            />
            <WorkDurationFields
              expectedWorkMinutes={form.expectedWorkMinutes}
              lunchBreakMinutes={form.lunchBreakMinutes}
              spreadMinutes={form.spreadMinutes}
              onExpectedChange={value =>
                setField('expectedWorkMinutes', value)
              }
              onLunchChange={value => setField('lunchBreakMinutes', value)}
              onSpreadChange={value => setField('spreadMinutes', value)}
              hintStyle={themed.hint}
            />
            <Text style={themed.hint}>
              Per working day. Monthly expected hours = expected work duration × Office Calendar working days for that month.
            </Text>
            <AppInput
              label="Minimum half day *"
              value={form.minimumHalfDayMinutes}
              onChangeText={value => setField('minimumHalfDayMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Late grace"
              value={form.lateGraceMinutes}
              onChangeText={value => setField('lateGraceMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Early exit grace"
              value={form.earlyExitGraceMinutes}
              onChangeText={value => setField('earlyExitGraceMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Short hours tolerance"
              value={form.shortHoursToleranceMinutes}
              onChangeText={value =>
                setField('shortHoursToleranceMinutes', value)
              }
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Maximum late"
              value={form.maximumLateMinutes}
              onChangeText={value => setField('maximumLateMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Maximum early exit"
              value={form.maximumEarlyExitMinutes}
              onChangeText={value => setField('maximumEarlyExitMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Working days per month (fallback)"
              value={form.workingDaysPerMonth}
              onChangeText={value => setField('workingDaysPerMonth', value)}
              keyboardType="number-pad"
            />
            <Text style={themed.hint}>
              Not used when Office Calendar holidays and weekly offs are configured. Each month uses that month's actual working days.
            </Text>
            <AppInput
              label="Missing checkout policy *"
              value={form.missingCheckoutPolicy}
              onChangeText={value => setField('missingCheckoutPolicy', value)}
              autoCapitalize="characters"
            />
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Allow multiple punches</Text>
              <Switch
                value={form.allowMultiplePunches}
                onValueChange={value => setField('allowMultiplePunches', value)}
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Allow manual attendance</Text>
              <Switch
                value={form.allowManualAttendance}
                onValueChange={value =>
                  setField('allowManualAttendance', value)
                }
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>
                Allow attendance correction
              </Text>
              <Switch
                value={form.allowAttendanceCorrection}
                onValueChange={value =>
                  setField('allowAttendanceCorrection', value)
                }
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>WFH eligible</Text>
              <Switch
                value={form.wfhEligible}
                onValueChange={value => setField('wfhEligible', value)}
              />
            </View>
            {isTimePermissionEnabled() ? <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>
                Time permission enabled
              </Text>
              <Switch
                value={form.permissionEnabled}
                onValueChange={value => setField('permissionEnabled', value)}
              />
            </View> : null}
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Overtime enabled</Text>
              <Switch
                value={form.overtimeEnabled}
                onValueChange={value => setField('overtimeEnabled', value)}
              />
            </View>
            {form.overtimeEnabled ? (
              <>
                <AppInput
                  label="Overtime after *"
                  value={form.overtimeAfterMinutes}
                  onChangeText={value =>
                    setField('overtimeAfterMinutes', value)
                  }
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholder={DURATION_INPUT_HINT}
                />
                <AppInput
                  label="Maximum overtime"
                  value={form.maximumOvertimeMinutes}
                  onChangeText={value =>
                    setField('maximumOvertimeMinutes', value)
                  }
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholder={DURATION_INPUT_HINT}
                />
              </>
            ) : null}
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Default policy</Text>
              <Switch
                value={form.isDefault}
                onValueChange={value => setField('isDefault', value)}
              />
            </View>
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
